import routes from 'const/routes'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import InactivityModal from 'pages/InactivityModal'
import React from 'react'
import ActivityModal from './ActivityModal'
import AddAccountModal from './AddAccountModal'
import CardStatusTrackerModal from './CardStatusTrackerModal'
import ChangePaymentDueDateModal from './ChangePaymentDueDateModal'
import CreditBalanceRefundDrawer from './CreditBalanceRefund'
import CreditLimitIncreaseModal from './CreditLimitIncreaseModal'
import CustomCardArtModal from './CustomCardArt'
import DigitalCollectionsDrawer from './DigitalCollections'
import MakePaymentModal from './MakeAPaymentModal'
import DisputesModal from './DisputesModal'
import ManageCardholderModal from './ManageCardholderModal'
import PaymentSecurityModal from './PaymentSecurity'
import ReplaceYourCardModal from './ReplaceYourCard'
import RequestPastStatementsModal from './RequestPastStatements'
import UpdateIncomeModal from './UpdateIncomeModal'
import ReportLostOrStolenModal from './ReportLostOrStolenCard'
import BalanceTransferOffers from './BalanceTransferOffers'

/**
 * Props for DashboardModals component
 * @property {string} locationPathname - Current route pathname
 * @property {boolean} isCreditAccountsAPIError - Whether there was an error fetching credit accounts
 */
interface DashboardModalsProps {
  locationPathname: string
  isCreditAccountsAPIError: boolean
}

/**
 * DashboardModals component
 *
 * Renders all dashboard-level modals and drawers, conditionally based on feature flags,
 * route, and error state. This component centralizes modal rendering for the dashboard.
 *
 * @param {DashboardModalsProps} props
 * @returns {JSX.Element | null}
 */
const DashboardModals: React.FC<DashboardModalsProps> = ({
  locationPathname,
  isCreditAccountsAPIError
}) => {
  // Do not render any modals on logout or account details routes
  if (
    locationPathname === routes.LOGOUT ||
    locationPathname === routes.ACCOUNT_DETAILS
  )
    return null
  return (
    <aside>
      {/* Always render the Activity modal */}
      <ActivityModal />
      {/* Conditionally render modals/drawers based on feature flags and state */}
      <CreditBalanceRefundDrawer />
      <CreditLimitIncreaseModal />
      <InactivityModal />
      <MakePaymentModal />
      {isFeatureFlagEnabled('isAnnualIncomeNotificationEnabled') && (
        <UpdateIncomeModal />
      )}
      {/* Add Account modal only if no API error */}
      {!isCreditAccountsAPIError && <AddAccountModal />}
      {isFeatureFlagEnabled('isBalanceTransfersEnabled') && (
        <BalanceTransferOffers />
      )}
      <ManageCardholderModal />
      <ChangePaymentDueDateModal />
      {isFeatureFlagEnabled('isCardStatusTrackerEnabled') && (
        <CardStatusTrackerModal />
      )}
      {/*
       * Custom Card Art modal — always mounted.
       * Email deep links and Marketing MFE tile clicks open it via modalStatusAtom.
       */}
      <CustomCardArtModal />
      {isFeatureFlagEnabled('isReplaceYourCardEnabled') && (
        <ReplaceYourCardModal />
      )}
      {isFeatureFlagEnabled('isReportLostOrStolenCardEnabled') && (
        <ReportLostOrStolenModal />
      )}
      {isFeatureFlagEnabled('isDigitalCollectionsExperienceEnabled') && (
        <DigitalCollectionsDrawer />
      )}
      {isFeatureFlagEnabled('isPaymentSecurityEnabled') && (
        <PaymentSecurityModal />
      )}
      {isFeatureFlagEnabled('isDocumentCenterEnabled') && (
        <RequestPastStatementsModal />
      )}
      <DisputesModal />
    </aside>
  )
}

export default DashboardModals
